import { useEffect, useRef, useState, useContext } from "react";
import AppContext from "../../../context";
import { useSearchParams, useNavigate } from "react-router-dom";

import Meta from "../../components/Meta";

import styles from '../../../styles/Entry.module.scss';

function Induction() {

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const context = useContext(AppContext);
	const VIDEO_ID = context?.domain?.inductionVideoId || "";
	const SEGMENT_SECONDS = 5;

	const watchedSegmentsRef = useRef(new Set());
	const totalSegmentsRef = useRef(0);
	const playerRef = useRef(null);
	const intervalIdRef = useRef(null);

	const [canContinue, setCanContinue] = useState(!context?.domain?.forceInductionVideoWatch);

	useEffect(() => {
		if(!VIDEO_ID) return;
		function onYouTubeReady() {
			playerRef.current = new window.YT.Player("yt-induction", {
				events: {
					onReady: (event) => {
						const initialize = () => {
							const duration = event.target.getDuration();
							if (duration && duration > 0) {
								totalSegmentsRef.current = Math.max(1, Math.ceil(duration / SEGMENT_SECONDS));
								startPolling();
							} else {
								setTimeout(initialize, 300);
							}
						};
						initialize();
					},
					onStateChange: (ev) => {
						if (ev.data === window.YT.PlayerState.ENDED) {
							checkProgress(true);
						}
					}
				}
			});
		}

		function ensureApiLoaded() {
			if (window.YT && window.YT.Player) {
				onYouTubeReady();
				return;
			}
			const existing = document.getElementById("youtube-iframe-api");
			if (!existing) {
				const script = document.createElement("script");
				script.src = "https://www.youtube.com/iframe_api";
				script.id = "youtube-iframe-api";
				document.body.appendChild(script);
			}
			const prev = window.onYouTubeIframeAPIReady;
			window.onYouTubeIframeAPIReady = () => {
				if (typeof prev === "function") prev();
				onYouTubeReady();
			};
		}

		function startPolling() {
			if (intervalIdRef.current) clearInterval(intervalIdRef.current);
			intervalIdRef.current = setInterval(() => checkProgress(false), 1000);
		}

		function checkProgress(force) {
			const player = playerRef.current;
			if (!player || typeof player.getCurrentTime !== "function") return;

			const state = player.getPlayerState && player.getPlayerState();
			if (!force && state !== window.YT.PlayerState.PLAYING) return;

			const currentTime = player.getCurrentTime();
			const duration = player.getDuration() || 0;
			if (!duration) return;

			let totalSegments = totalSegmentsRef.current || Math.max(1, Math.ceil(duration / SEGMENT_SECONDS));
			totalSegmentsRef.current = totalSegments;

			const index = Math.min(totalSegments - 1, Math.floor(currentTime / SEGMENT_SECONDS));
			const set = watchedSegmentsRef.current;
			set.add(index);

			const coverage = set.size / totalSegments;
			if (coverage >= 0.97 && context?.domain?.forceInductionVideoWatch) {
				if (!canContinue) setCanContinue(true);
				if (intervalIdRef.current) {
					clearInterval(intervalIdRef.current);
					intervalIdRef.current = null;
				}
			}
		}

		ensureApiLoaded();

		return () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current);
				intervalIdRef.current = null;
			}
			try {
				if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
			} catch (e) {}
		};
	}, [VIDEO_ID]);

	const iframeSrc = "https://www.youtube.com/embed/" + VIDEO_ID
		+ "?enablejsapi=1"
		+ "&origin=" + window.location.origin
		+ "&rel=0&modestbranding=1&playsinline=1&controls=1&disablekb=1";

	return (
		<>
			<Meta title="Video Induction" />

			<div className={styles.fullHeight}>
				<div>
					<h1>Induction</h1>
					<p>{context?.domain?.inductionVideoText || "Please watch the induction video below carefully."}</p>

					{VIDEO_ID ? <iframe
						id="yt-induction"
						className={styles.youtube}
						src={iframeSrc}
						title="YouTube video player"
						frameBorder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
					/> : null}

				</div>

				<div className={styles.actions}>
					{searchParams.get("request") === "true" ?
						<p className={styles.requested}>We have received your request for a face-to-face induction and will be in touch soon.</p>
					: null}
					<button
						className="btn btnPrimary"
						disabled={!canContinue}
						onClick={() => navigate("/entry/terms")}
					>
						{!canContinue && context?.domain?.forceInductionVideoWatch ? "Watch the full video to continue" : "Continue"}
					</button>
				</div>
			</div>
		</>
	);
}

export default Induction;
